import EditIcon from "@mui/icons-material/Edit";
import { skipToken } from "@reduxjs/toolkit/query";
import axios from "axios";
import FileDownload from "js-file-download";
import moment from "moment";
import { useState, useEffect, useCallback } from "react";
import { Container, Row, Col, Button, Alert } from "react-bootstrap";
import { Outlet, useParams } from "react-router-dom";

import { AUTH_ROLES, PERMISSION_AREAS } from "api/common/types/auth.types";
import { useGetNistByProductIdQuery } from "api/memberPortal/certReview/certReview.api";
import { useProductCertificateQuery } from "api/memberPortal/files/files.api";
import { useMyOrgQuery } from "api/memberPortal/orgs/orgs.api";
import { useCheckInMeetingByProductQuery } from "api/memberPortal/project/checkinMeeting/checkinMeeting.api";
import { useMaintenancesByProductQuery } from "api/memberPortal/project/maintenance/maintenance.api";
import {
  useIsValidatorQuery,
  useProductValidatorsQuery,
} from "api/memberPortal/project/personnel/personnel.api";
import {
  useGetProductQuery,
  useProductPermissionCheckQuery,
} from "api/memberPortal/project/product/product.api";
import {
  PRODUCT_KICKOFF_STATUSES,
  PRODUCT_STATUSES,
} from "api/memberPortal/project/product/productStatuses.type";
import { useSerializedProjectPpQuery } from "api/memberPortal/project/protectionProfiles/protectionProfiles.api";
import Notes from "components/Notes/Notes";
import { useLoading } from "components/UI/LoadingSpinner/UseLoading";
import UnauthorizedView from "components/UI/UnauthorizedView";
import { useAuth } from "hooks/useAuth";
import { useInterval } from "hooks/useInterval";
import { usePageTitle } from "hooks/usePageTitle";
import { getISOCurrentDate } from "utils/timeUtils";

import FileUploadModal from "../FileUpload";

import CertificateModal from "./CertificateModal/CertificateModal";
import PCLReviewDetails from "./PCLReviewDetails";
import NonEditableAccordion from "./ProductDetailsAccordions/NonEditableAccordion";
import PostingAccordion from "./ProductDetailsAccordions/PostingAccordion";
import ProjectDetailsAccordion from "./ProductDetailsAccordions/ProjectDetailsAccordion";
import PostingHeader from "./ProductDetailsHeaders/PostingHeader";
import ProductDetailsHeaderInfo from "./ProductDetailsHeaders/ProductDetailsHeaderInfo";
import WithdrawalTerminationFeedbackHeader from "./ProductDetailsHeaders/WithdrawalTerminationFeedbackHeader";
import PostingSideInfo from "./ProductDetailSideInfo/PostingSideInfo";
import SideInfo from "./ProductDetailSideInfo/SideInfo";
import NiapClearPostingVerifier from "./ProductDetailsSubheaderDetails/NiapClearPostingVerifier";
import PostingSubheaderDetails from "./ProductDetailsSubheaderDetails/PostingSubheaderDetails";
import ProductDetailsSubheader from "./ProductDetailsSubheaderDetails/ProductDetailsSubheader";
import ProductKickoffModal from "./ProductKickoffModal";
import InitiateProgressModal from "./ProgressPoints/ProgressPointModals/InitiateProgressModal";
import ScheduleMeetingModal from "./ProgressPoints/ProgressPointModals/ProgressPoint2/ScheduleMeetingModal.jsx";
import ProgressPointModal345 from "./ProgressPoints/ProgressPointModals/ProgressPoint345/ProgressPointModal345";
import ProgressPointEditModal7 from "./ProgressPoints/ProgressPointModals/ProgressPoint67/ProgressPointEditModal7.jsx";
import ProgressPointModal6 from "./ProgressPoints/ProgressPointModals/ProgressPoint67/ProgressPointModal6.jsx";
import ValidatorCheckoutApprovalModal from "./ProgressPoints/ProgressPointModals/ProgressPoint67/ValidatorCheckoutApprovalModal.jsx";
import ValidatorCheckoutRejectionModal from "./ProgressPoints/ProgressPointModals/ProgressPoint67/ValidatorCheckoutRejectionModal";
import ValidatorRecommendPostingModal from "./ProgressPoints/ProgressPointModals/ProgressPoint67/ValidatorRecommendPostingModal";
import ProgressPointEditModal8 from "./ProgressPoints/ProgressPointModals/ProgressPoint8/ProgressPointEditModal8.jsx";
import ProgressPoints from "./ProgressPoints/ProgressPoints";
import ValidatorKickoffModal from "./ValidatorKickoffModal";

import "../Products.css";
import "./ProductDetails.css";

const EMPTY_OBJECT = {};

const CheckProductPermDetails = () => {
  const { permissions } = useAuth();
  const { id } = useParams();

  const { data: hasPerm } = useProductPermissionCheckQuery(id ?? skipToken);

  if (
    (permissions.role_permissions?.find(
      (permission) => permission.area === PERMISSION_AREAS.PROJECT,
    )?.local_only &&
      !hasPerm) ||
    permissions.role_type === AUTH_ROLES.VENDOR
  ) {
    return <UnauthorizedView />;
  } else if (
    hasPerm &&
    permissions.role_permissions?.find(
      (permission) => permission.area === PERMISSION_AREAS.PROJECT,
    )?.local_only
  ) {
    return <ProductDetails />;
  } else if (hasPerm === true) {
    //this would be for NIAP and Validators
    return <ProductDetails />;
  }
  return <div></div>;
};

function ProductDetails() {
  usePageTitle("NIAP Community - Product Details");

  const { authToken, csrfToken, currentUser, permissions } = useAuth();
  const { id } = useParams();

  const [show, setShow] = useState(false);
  const [showKickoffModal, setShowKickoffModal] = useState(false);
  const [showValidatorKickoffModal, setShowValidatorKickoffModal] =
    useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [files, setFiles] = useState([]);

  // TODO: see wtf is going on with this right here.
  // notes are unused, why are we fetching?
  const [refetchNotes, setRefetchNotes] = useState(false);
  const [
    showValidatorRecommendPostingModal,
    setShowValidatorRecommendPostingModal,
  ] = useState(false);
  const [showPPModal345, setShowPPModal345] = useState(false);
  const [showPPModal6, setShowPPModal6] = useState(false);
  const [showPPModal7, setShowPPModal7] = useState(false);
  const [showPPModal8, setShowPPModal8] = useState(false);
  const [showInitiateProgressModel, setShowInitiateProgressModel] =
    useState(false);
  const [progressPointModalReadOnly, setProgressPointModalReadOnly] =
    useState(false);
  const [progressPointEdit, setProgressPointEdit] = useState(false);
  const [progressPoints, setProgressPoints] = useState([]);
  const [alert, setAlert] = useState({});
  const [currentPP, setCurrentPP] = useState({});
  const [kickoffAlert, setKickoffAlert] = useState({});
  const [updateSidebarFiles, setUpdateSidebarFiles] = useState(0);

  const [showValidatorCheckoutModal, setShowValidatorCheckoutModal] =
    useState(false);
  const [
    showValidatorApproveCheckoutModal,
    setShowValidatorApproveCheckoutModal,
  ] = useState(false);
  const [validationReport, setValidationReport] = useState({});
  const [finalDraftValidationReport, setFinalDraftValidationReport] = useState(
    {},
  );
  const [readyToPostChecks, setReadyToPostChecks] = useState([]);
  const [signedCheckIn, setSignedCheckIn] = useState({});
  const [labSignedCheckout, setLabSignedCheckout] = useState({});
  const [vendorSignedCheckout, setVendorSignedCheckout] = useState({});
  const [securityTarget, setSecurityTarget] = useState({});

  const [assuranceActivityReport, setAssuranceActivityReport] = useState({});
  const [showCertificate, setShowCertificate] = useState(false);
  const [rejectionType, setRejectionType] = useState("");

  // TODO: Get rid of these stupid refetches.
  // I'm only doing this rn because i'm lazy and they are used over 30 times in this file TT
  // Will need to convert all components they are passed to to RTKQ in the process
  const { data: product = EMPTY_OBJECT, refetch: loadProject } =
    useGetProductQuery(id);
  const { data: maintenances, refetch: fetchProjectMaintenance } =
    useMaintenancesByProductQuery(id);
  const { data: projpps, refetch: fetchProductPP } =
    useSerializedProjectPpQuery(id);
  const { data: meeting = EMPTY_OBJECT, refetch: getMeeting } =
    useCheckInMeetingByProductQuery(product.product_id ?? skipToken);

  const { data: certificate } = useProductCertificateQuery({
    file_type: "product",
    file_type_id: id,
  });
  const { data: validationTeam } = useProductValidatorsQuery(id);
  const { data: isValidator } = useIsValidatorQuery(id);
  const { data: myOrg } = useMyOrgQuery(
    {},
    {
      refetchOnMountOrArgChange: true, // this fixes emulation weirdness
    },
  );
  const { data: nistReview, refetch: getNistReview } =
    useGetNistByProductIdQuery(product.product_id ?? skipToken);

  const isMemberOfAssignedLab =
    myOrg?.name === product.assigned_lab?.name &&
    permissions.role_type === "Lab";

  const userIsNiap = permissions.role_type === "NIAP";

  const { showLoadingSpinner, hideLoadingSpinner } = useLoading();

  const handleCloseProgressPointModal = (product, action) => {
    setShowPPModal345(false);
    setShowPPModal6(false);
    setShowPPModal7(false);
    setShowPPModal8(false);
    setProgressPointModalReadOnly(false);
    setProgressPointEdit(false);
    pullFiles();
    if (product) {
      if (action) {
        if (action === "approved") {
          setAlert({
            variant: "success",
            message: "Progress Point Successfully Approved",
          });
        } else if (action === "rejected") {
          setAlert({
            variant: "dark",
            message: "Progress Point Successfully Rejected",
          });
        } else if (action === "submitting") {
          setAlert({
            variant: "success",
            message: "Progress Point Successfully Submitted",
          });
        } else {
          setAlert({
            variant: "success",
            message: "Progress Point Successfully Updated",
          });
        }
      } else {
        setAlert({
          variant: "success",
          message: "Progress Point Successfully Updated",
        });
      }
    }
  };

  const handleCloseInitiateProgressModal = (action) => {
    setShowInitiateProgressModel(false);
    if (action === "initiate") {
      setAlert({
        variant: "success",
        message: "Progress Point Successfully Initiated",
      });
    } else {
      setAlert({
        variant: "success",
        message: "Progress Point Est. Completion Date Saved",
      });
    }
  };

  const handleCloseValidatorKickoffModal = () => {
    setShowValidatorKickoffModal(false);
  };

  const handleCloseProductKickoffModal = (product, action) => {
    if (product) {
      if (action === "rejected") {
        setKickoffAlert({
          variant: "success",
          message: "Successfully Rejected Kickoff Recommendation",
        });
      } else {
        setKickoffAlert({
          variant: "success",
          message: "Successfully Approved Kickoff Recommendation",
        });
        getProgressPoints();
      }
    }
    setShowKickoffModal(false);
  };

  // TODO: Replace with RTKQ
  const downloadFile = (file) => {
    showLoadingSpinner("Downloading");
    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}file/get_file/?file_id=${file.file_id}`,
        {
          withCredentials: true,
          headers: {
            Authorization: `Token ${authToken}`,
          },
          responseType: "blob",
        },
      )
      .then((response) => {
        hideLoadingSpinner();

        FileDownload(
          response.data,
          file.file_new_name ? file.file_new_name : file.file_name,
        );
      })
      .catch(() => {
        console.log("Error");
      });
  };

  const fetchBlankCertification = async () => {
    // TODO: useGenerateDocumentQuery created - just need to swap out
    await axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}file/generate_document/?document_name=Draft or Final Certificate&product_id=${product?.product_id}&cert_review_option=final`,
        {
          withCredentials: true,
          headers: { Authorization: `Token ${authToken}` },
          responseType: "blob",
        },
      )
      .then((response) => {
        let url = "";
        if (response.data.size > 0) {
          url = URL.createObjectURL(response.data);
        }
        const link = document.createElement("a");
        link.href = url;

        let fileName = `VID${product?.v_id}-FINAL CERT`;

        link.setAttribute("download", fileName);

        document.body.appendChild(link); // Required for FF
        link.click(); // This will download the file

        window.URL.revokeObjectURL(url); // Clean up by revoking the blob URL
        link.remove(); // Clean up by removing the link
      })
      .catch((error) => {
        console.log("error fetching blank certificate", error);
      });
  };

  function refreshPage() {
    window.location.reload(false);
  }

  useEffect(() => {
    if (permissions.role_type === "NIAP") {
      const updatedChecks = [
        {
          "ready-to-post-0": true,
        },
        {
          "ready-to-post-1": true,
        },
        {
          "ready-to-post-2": true,
        },
        {
          "ready-to-post-3": true,
        },
        {
          "ready-to-post-public-files": true,
        },
      ];
      setReadyToPostChecks(updatedChecks);
    }
  }, [permissions.role_type]);

  //updates readyToPost state with checked value
  const handleReadyToPostCheck = (dictChecks) => {
    const updatedChecks = [...readyToPostChecks];
    const key = Object.keys(dictChecks)[0];
    const index = updatedChecks.findIndex((obj) => Object.keys(obj)[0] === key);
    if (index !== -1) {
      updatedChecks[index] = dictChecks;
    } else {
      updatedChecks.push(dictChecks);
    }
    setReadyToPostChecks(updatedChecks);
  };

  // TODO: Replace with RTKQ
  const handleCertifyProduct = async () => {
    await axios
      .put(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}project/progress/update_progress/`,
        {
          product: product?.product_id,
          progress_point: 8,
          status: "Completed",
          new_submission_date: true,
        },
        {
          withCredentials: true,
          headers: {
            "X-CSRFToken": csrfToken,
            Authorization: `Token ${authToken}`,
          },
        },
      )
      .then((response) => {
        const note = `Product posted to PCL and CC Portal on ${moment().format("MM/DD/YYYY")}.`;
        createNote(note);
        if (response.data.status === "Update Already Exists") {
          refreshPage();
        } else {
          // TODO: Replace with RTKQ
          axios
            .put(
              `${import.meta.env.VITE_DJANGO_ENDPOINT}project/product/${product?.product_id}/`,
              {
                status: "Certified",
                certification_status: "Certified",
                certification_date:
                  product?.certification_date !== null
                    ? product?.certification_date
                    : new Date(
                        new Date().setMinutes(
                          new Date().getMinutes() -
                            new Date().getTimezoneOffset(),
                        ),
                      ),
                assurance_maintenance_date:
                  product?.assurance_maintenance_date !== null
                    ? product?.assurance_maintenance_date
                    : moment.utc().add(2, "years").format("YYYY-MM-DD"),
                posted_date: new Date(
                  new Date().setMinutes(
                    new Date().getMinutes() - new Date().getTimezoneOffset(),
                  ),
                ),
              },
              {
                withCredentials: true,
                headers: {
                  "X-CSRFToken": csrfToken,
                  Authorization: `Token ${authToken}`,
                },
              },
            )
            .then(() => {
              getProgressPoints();
              loadProject();
            })
            .catch((error) => {
              console.log(error);
            });
        }
      })
      .catch((error) => console.log(error));
  };

  // TODO: Replace with RTKQ
  const createNote = async (note) => {
    await axios
      .post(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}note/`,
        {
          note: note,
          note_type: "product",
          note_type_id: id,
          submitted_on: getISOCurrentDate(),
        },
        {
          withCredentials: true,
          headers: {
            "X-CSRFToken": csrfToken,
            Authorization: `Token ${authToken}`,
          },
        },
      )
      .then((response) => {
        setRefetchNotes(response?.data?.note_id);
      })
      .catch((error) => console.log("Unable to add note: " + error));
  };

  useEffect(() => {
    if (id) {
      setKickoffAlert({});
    }
  }, [id]);

  const pullFiles = useCallback(() => {
    // TODO: this is more complex so im holding off till im less rushed
    if (id) {
      axios
        .get(
          `${import.meta.env.VITE_DJANGO_ENDPOINT}file/get_all_files_by_type_and_type_id/?file_type=product&file_type_id=${id}`,
          {
            withCredentials: true,
            headers: {
              Authorization: `Token ${authToken}`,
            },
          },
        )
        .then((response) => {
          setFiles(response.data);
          // This will return an object with keys of file_display_name
          // and values of an array of all files with that file_display_name
          const files = Object.groupBy(
            response.data,
            (file) => file.file_display_name,
          );

          const getLatestFile = (fileDisplayName) => {
            return (
              // In this function we access the array corresponding to the file_display_name
              // and reduce it down to a single value of the most recently uploaded file
              files[fileDisplayName]?.reduce((mostRecentFile, fileToCheck) => {
                // if we don't have an uploaded_on date to compare to we are comparing to the epoch
                return moment(fileToCheck.uploaded_on).isAfter(
                  moment(mostRecentFile.uploaded_on || 0),
                )
                  ? fileToCheck
                  : mostRecentFile;
                // if we don't have a valid fileDisplayName we return an empty object
              }, {}) || {}
            );
          };

          setValidationReport(getLatestFile("Validation Report"));
          setFinalDraftValidationReport(
            getLatestFile("Final Draft Validation Report"),
          );
          setSecurityTarget(getLatestFile("Security Target"));
          setAssuranceActivityReport(
            getLatestFile("Assurance Activity Report (AAR)"),
          );
          setSignedCheckIn(getLatestFile("Vendor Signed Check In Package"));

          // leaving both possible names here for checkout until we can remove the old one
          let labROI = getLatestFile("CCTL Signed Check Out Package");
          if (Object.keys(labROI).length === 0) {
            labROI = getLatestFile("CCTL Signed ROI");
          }
          setLabSignedCheckout(labROI);

          let vendorROI = getLatestFile("Vendor Signed Check Out Package");
          if (Object.keys(vendorROI).length === 0) {
            vendorROI = getLatestFile("Vendor Signed ROI");
          }
          setVendorSignedCheckout(vendorROI);
        })
        .catch(() => {
          console.log("Error getting files");
        });
    }
  }, [authToken, id]);

  /*
    Requests validators amend checkout package by setting PP6 status to "In Progress". 
    PP7 status stays the same and progress point counter is updated
  */
  const amendedPackageNeeded = () => {
    let resubmissionCounter = progressPoints[5]?.resubmission_counter;
    if (resubmissionCounter === null) {
      resubmissionCounter = 0;
    } else {
      resubmissionCounter += 1;
    }
    // TODO: Replace with RTKQ
    axios
      .put(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}project/progress/update_progress/`,
        {
          product: product?.product_id,
          progress_point: 6,
          status: "Amended Package Needed",
          resubmission_counter: resubmissionCounter,
        },
        {
          withCredentials: true,
          headers: {
            "X-CSRFToken": csrfToken,
            Authorization: `Token ${authToken}`,
          },
        },
      )
      .then(() => {
        // TODO: Replace with RTKQ
        axios
          .put(
            `${import.meta.env.VITE_DJANGO_ENDPOINT}project/progress/update_progress/`,
            {
              product: product?.product_id,
              progress_point: 7,
              resubmission_counter: resubmissionCounter,
              status: "Awaiting Changes",
            },
            {
              withCredentials: true,
              headers: {
                "X-CSRFToken": csrfToken,
                Authorization: `Token ${authToken}`,
              },
            },
          )
          .then(() => {
            getProgressPoints();
            setAlert({
              variant: "success",
              message: "Progress Point Successfully Released To Labs",
            });
          })
          .catch((error) => {
            console.log("error updating progress point", error);
          });
      })
      .catch((error) => {
        console.log("error updating progress point", error);
      });
  };

  // TODO: This is now in RTK Query, but this function is very tightly coupled to the rest of the component
  // so it will take some elbow grease to get it fully removed
  const getProgressPoints = useCallback(() => {
    if (product?.product_id) {
      axios
        .get(
          `${import.meta.env.VITE_DJANGO_ENDPOINT}project/progress/progress_points_by_pid/?pid=${product?.product_id}`,
          {
            withCredentials: true,
            headers: { Authorization: `Token ${authToken}` },
          },
        )
        .then((response) => {
          setProgressPoints(
            response.data.sort((a, b) =>
              a.progress_point > b.progress_point ? 1 : -1,
            ),
          );
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [authToken, product?.product_id]);

  const updateFiles = () => {
    setShow(false);
    pullFiles();
    setUpdateSidebarFiles(updateSidebarFiles + 1);
  };

  useEffect(() => {
    getProgressPoints();
  }, [getProgressPoints]);

  useInterval(() => {
    getProgressPoints();
  }, 60000); //1 min

  useEffect(() => {
    pullFiles();
  }, [pullFiles]);

  useInterval(() => {
    pullFiles();
  }, 180000); //3 min

  const getEditButton = (pp) => {
    if (
      permissions?.role_type === "Lab" &&
      (product?.status === "Certified" ||
        product?.status === "In Assurance Maintenance")
    ) {
      return <></>;
    }
    switch (pp?.progress_point) {
      case 2:
        if (
          Object.keys(meeting).length > 0 &&
          permissions?.role_type === "NIAP"
        ) {
          return (
            <EditIcon
              style={{ width: "15px !important", height: "15px !important" }}
              className="edit-button clickable"
              onClick={() => {
                setProgressPointEdit(true);
                setShowScheduleModal(true);
              }}
            />
          );
        } else {
          return <></>;
        }
      case 3:
      case 4:
      case 5:
        if (
          permissions?.role_type === "Lab" ||
          permissions?.role_type === "NIAP"
        ) {
          return (
            <EditIcon
              style={{ width: "15px !important", height: "15px !important" }}
              className="edit-button clickable"
              onClick={() => {
                setCurrentPP(pp);
                setProgressPointEdit(true);
                setShowPPModal345(true);
              }}
            />
          );
        } else {
          return <></>;
        }
      case 6:
        if (
          permissions?.role_type === "Lab" ||
          permissions?.role_type === "NIAP"
        ) {
          return (
            <EditIcon
              style={{ width: "15px !important", height: "15px !important" }}
              className="edit-button clickable"
              onClick={() => {
                setCurrentPP(pp);
                setProgressPointEdit(true);
                setShowPPModal6(true);
              }}
            />
          );
        } else {
          return <></>;
        }
      case 7:
        if (
          (permissions?.role_type === "Validator" && isValidator) ||
          permissions?.role_type === "NIAP"
        ) {
          return (
            <EditIcon
              style={{ width: "15px !important", height: "15px !important" }}
              className="edit-button clickable"
              onClick={() => {
                setCurrentPP(pp);
                setProgressPointEdit(true);
                setShowPPModal7(true);
              }}
            />
          );
        } else {
          return <></>;
        }
      case 8:
        if (permissions?.role_type === "NIAP") {
          return (
            <EditIcon
              style={{ width: "15px !important", height: "15px !important" }}
              className="edit-button clickable"
              onClick={() => {
                setCurrentPP(pp);
                setProgressPointEdit(true);
                setShowPPModal8(true);
              }}
            />
          );
        } else {
          return <></>;
        }
      default:
        return <></>;
    }
  };

  const showButtons = (pp) => {
    switch (pp?.status) {
      case "Est. Completion Date Set":
      case "Not Started":
        switch (pp?.progress_point) {
          case 2:
            if (
              Object.keys(meeting).length === 0 &&
              permissions?.role_type === "NIAP"
            ) {
              return (
                <Button
                  size="sm"
                  variant="warning"
                  className="rounded-pill"
                  onClick={() => {
                    setCurrentPP(pp);
                    setShowScheduleModal(true);
                  }}
                >
                  Schedule Meeting
                </Button>
              );
            } else {
              return <></>;
            }
          case 3:
          case 4:
          case 5:
            if (
              progressPoints[1]?.status === "Completed" &&
              permissions?.role_type === "Lab"
            ) {
              return (
                <Button
                  size="sm"
                  variant="warning"
                  className="rounded-pill small-font"
                  onClick={() => {
                    setCurrentPP(pp);
                    setShowPPModal345(true);
                  }}
                >
                  Mark In-Progress
                </Button>
              );
            } else {
              return <></>;
            }
          case 6:
            if (
              progressPoints[2]?.status === "Completed" &&
              progressPoints[3]?.status === "Completed" &&
              progressPoints[4]?.status === "Completed" &&
              permissions?.role_type === "Lab"
            ) {
              return (
                <Button
                  size="sm"
                  variant="warning"
                  className="rounded-pill small-font"
                  onClick={() => {
                    setCurrentPP(pp);
                    setShowInitiateProgressModel(true);
                  }}
                >
                  Initiate CheckOut
                </Button>
              );
            } else {
              return <></>;
            }
          default:
            return;
        }
      case "In Progress":
        switch (pp?.progress_point) {
          case 2:
            if (
              Object.keys(meeting).length === 0 &&
              permissions?.role_type === "NIAP"
            ) {
              return (
                <Button
                  size="sm"
                  variant="warning"
                  className="rounded-pill"
                  onClick={() => {
                    setCurrentPP(pp);
                    setShowScheduleModal(true);
                  }}
                >
                  Schedule Meeting
                </Button>
              );
            } else {
              return <></>;
            }
          case 3:
          case 4:
          case 5:
            if (permissions?.role_type === "Lab") {
              return (
                <Button
                  size="sm"
                  variant="success"
                  className="rounded-pill small-font"
                  onClick={() => {
                    setCurrentPP(pp);
                    setProgressPointModalReadOnly(true);
                    setShowPPModal345(true);
                  }}
                >
                  Mark Complete
                </Button>
              );
            } else {
              return <></>;
            }
          case 6:
            if (permissions?.role_type === "Lab") {
              return (
                <>
                  <Button
                    size="sm"
                    variant="warning"
                    className="rounded-pill small-font"
                    onClick={() => {
                      setCurrentPP(pp);
                      setShowPPModal6(true);
                    }}
                  >
                    {progressPoints[5].status === "Rejected"
                      ? "Resubmit"
                      : "Submit"}{" "}
                    Checkout Package
                  </Button>
                </>
              );
            } else {
              return <></>;
            }
          case 7:
            if (
              (permissions?.role_type === "Validator" ||
                permissions?.role_type === "NIAP") &&
              isValidator
            ) {
              return (
                <Container>
                  <Row className="d-flex justify-content-center">
                    {progressPoints[5].status !== "In Progress" &&
                      progressPoints[5].status !== "Rejected" &&
                      (permissions?.is_senior === true ||
                        permissions?.is_lead === true ||
                        (permissions?.role_type === "NIAP" && isValidator)) && (
                        <Col sm={10}>
                          <Row className="mb-1">
                            <Button
                              size="sm"
                              variant="warning"
                              className="rounded-pill small-font p-1"
                              onClick={() => {
                                setCurrentPP(progressPoints[5]);
                                setRejectionType("Amended Package");
                                amendedPackageNeeded();
                              }}
                            >
                              Amended Package Needed
                            </Button>
                          </Row>

                          <Row className="mb-1">
                            <Button
                              size="sm"
                              variant="success"
                              className="rounded-pill small-font p-1"
                              onClick={() => {
                                setCurrentPP(pp);
                                setShowValidatorApproveCheckoutModal(true);
                              }}
                            >
                              Submit Final Validator Check
                            </Button>
                          </Row>
                        </Col>
                      )}
                  </Row>
                </Container>
              );
            } else {
              return <></>;
            }
          case 8:
            if (
              progressPoints[6]?.status === "Completed" &&
              permissions?.role_type === "NIAP"
            ) {
              return (
                <Button
                  size="sm"
                  variant="success"
                  className="rounded-pill small-font"
                  //disabled if checks are less than 5 or include an unchecked option
                  disabled={
                    readyToPostChecks.length < 5 ||
                    readyToPostChecks.some((obj) =>
                      Object.values(obj).includes(false),
                    )
                  }
                  onClick={handleCertifyProduct}
                >
                  Post to PCL
                </Button>
              );
            } else {
              return <></>;
            }
          default:
            return;
        }
      case "Awaiting Checkout":
        switch (pp?.progress_point) {
          case 6:
            if (
              (permissions?.role_type === "Validator" ||
                permissions?.role_type === "NIAP") &&
              isValidator &&
              progressPoints[5]?.status === "Awaiting Checkout" &&
              progressPoints[5]?.resubmission_counter == null &&
              progressPoints[6]?.status !== "Ready for Release"
            ) {
              return (
                <Button
                  size="sm"
                  variant="danger"
                  className="rounded-pill small-font"
                  onClick={() => {
                    setCurrentPP(pp);
                    setRejectionType("Rejection");
                    setShowValidatorCheckoutModal(true);
                  }}
                >
                  Reject Package
                </Button>
              );
            } else {
              return <></>;
            }
          default:
            return;
        }
      case "Resubmitted":
        switch (pp?.progress_point) {
          case 6:
            if (
              (permissions?.role_type === "Validator" ||
                permissions?.role_type === "NIAP") &&
              isValidator &&
              progressPoints[6]?.status !== "Ready for Release"
            ) {
              return (
                <Button
                  size="sm"
                  variant="danger"
                  className="rounded-pill small-font"
                  onClick={() => {
                    setCurrentPP(pp);
                    setRejectionType("Rejection");
                    setShowValidatorCheckoutModal(true);
                  }}
                >
                  Reject Package
                </Button>
              );
            } else {
              return <></>;
            }
          default:
            return;
        }
      case "Ready for Release":
        switch (pp?.progress_point) {
          case 7:
            if (
              (permissions?.role_type === "Validator" &&
                isValidator === true &&
                permissions?.is_senior === true) ||
              (permissions?.role_type === "NIAP" && isValidator)
            ) {
              return (
                <Button
                  size="sm"
                  variant="success"
                  className="rounded-pill small-font"
                  disabled={
                    readyToPostChecks.length < 5 ||
                    readyToPostChecks.some((obj) =>
                      Object.values(obj).includes(false),
                    )
                  }
                  onClick={() => {
                    setCurrentPP(pp);
                    setShowValidatorRecommendPostingModal(true);
                  }}
                >
                  Recommend for Posting
                </Button>
              );
            } else {
              return <></>;
            }
          default:
            return;
        }
      case "Completed":
        switch (pp?.progress_point) {
          case 1:
            if (
              isValidator &&
              (permissions.is_senior || userIsNiap) &&
              product?.status === PRODUCT_STATUSES.PRE_EVALUATION &&
              product?.kickoff_status ===
                PRODUCT_KICKOFF_STATUSES.READY_FOR_RECOMMENDATION &&
              product?.rejected_rationale !== null &&
              product?.rejected_rationale !== ""
            ) {
              return (
                <Button
                  size="sm"
                  variant="warning"
                  className="rounded-pill small-font"
                  onClick={() => {
                    setCurrentPP(pp);
                    setShowValidatorKickoffModal(true);
                  }}
                >
                  Update Kickoff Recommendation
                </Button>
              );
            } else if (
              isValidator &&
              (permissions.is_senior || userIsNiap) &&
              product?.status === PRODUCT_STATUSES.PRE_EVALUATION &&
              product?.kickoff_status ===
                PRODUCT_KICKOFF_STATUSES.READY_FOR_RECOMMENDATION
            ) {
              return (
                <Button
                  size="sm"
                  variant="warning"
                  className="me-3 mb-2 rounded-pill"
                  onClick={() => {
                    setCurrentPP(pp);
                    setShowValidatorKickoffModal(true);
                  }}
                >
                  Kickoff Recommendation
                </Button>
              );
            } else if (
              permissions?.role_type === "NIAP" &&
              product?.status === PRODUCT_STATUSES.PRE_EVALUATION &&
              product?.kickoff_status ===
                PRODUCT_KICKOFF_STATUSES.RECOMMENDED_FOR_KICKOFF
            ) {
              return (
                <Button
                  size="sm"
                  variant="warning"
                  className="rounded-pill"
                  onClick={() => {
                    setCurrentPP(pp);
                    setShowKickoffModal(true);
                  }}
                >
                  Kickoff Recommendation
                </Button>
              );
            } else {
              return <></>;
            }
          case 2:
            if (
              Object.keys(meeting).length === 0 &&
              permissions?.role_type === "NIAP"
            ) {
              return (
                <Button
                  size="sm"
                  variant="warning"
                  className="rounded-pill"
                  onClick={() => {
                    setCurrentPP(pp);
                    setShowScheduleModal(true);
                  }}
                >
                  Schedule Meeting
                </Button>
              );
            } else {
              return <></>;
            }
          case 3:
          case 4:
          case 5:
            return (
              <Container>
                <Row>
                  <Col>
                    <Row>
                      <small className="small-font">Submitted by</small>
                    </Row>
                    <Row>
                      <small className="small-font">
                        {pp?.editor
                          ? pp?.editor?.first_name + " " + pp?.editor?.last_name
                          : pp?.creator?.first_name +
                            " " +
                            pp?.creator?.last_name}
                      </small>
                    </Row>
                    <Row>
                      <small className="mb-3 small-font">
                        on{" "}
                        {moment
                          .utc(pp?.submission_dates?.[0]?.estimated_date)
                          .format("MM/DD/YYYY")}
                      </small>
                    </Row>
                  </Col>
                </Row>
              </Container>
            );
          case 6:
            return (
              <Container>
                <Row>
                  <Col>
                    <Row>
                      <small className="small-font">Submitted by</small>
                    </Row>
                    <Row>
                      <small className="small-font">
                        {pp?.editor
                          ? pp?.editor?.first_name + " " + pp?.editor?.last_name
                          : pp?.creator?.first_name +
                            " " +
                            pp?.creator?.last_name}
                      </small>
                    </Row>
                    <Row>
                      <small className="mb-3 small-font">
                        on{" "}
                        {moment
                          .utc(
                            pp?.submission_dates?.[
                              pp?.submission_dates?.length - 1
                            ]?.estimated_date,
                          )
                          .format("MM/DD/YYYY")}
                      </small>
                    </Row>
                  </Col>
                </Row>
              </Container>
            );
          case 7:
          default:
            return (
              <Container>
                <Row>
                  <Col>
                    <Row>
                      <small className="small-font">Submitted by</small>
                    </Row>
                    <Row>
                      <small className="small-font">
                        {pp?.editor
                          ? pp?.editor?.first_name + " " + pp?.editor?.last_name
                          : pp?.creator?.first_name +
                            " " +
                            pp?.creator?.last_name}
                      </small>
                    </Row>
                    <Row>
                      <small className="mb-3 small-font">
                        on{" "}
                        {moment
                          .utc(
                            pp?.submission_dates?.[
                              pp?.submission_dates?.length - 1
                            ]?.estimated_date,
                          )
                          .format("MM/DD/YYYY")}
                      </small>
                    </Row>
                  </Col>
                </Row>
              </Container>
            );
        }
      default:
        return <></>;
    }
  };

  // TODO: This is used ALL over the place.
  // it will become useless once we finish the conversion to RTKQ
  // Until then im leaving it in place like it is.
  // I hate doing it this way,but it will keep things working as before
  const refresh = () => {
    loadProject();
    fetchProductPP();
    fetchProjectMaintenance();
  };

  const showPageComponents = () => {
    if (
      //validator final review page
      (permissions?.role_type === "Validator" ||
        permissions?.role_type === "NIAP") &&
      isValidator &&
      product.cctl_checkout_signer !== null &&
      product.vendor_checkout_signer !== null &&
      product.labs_cc_draft_cert_verifier !== null &&
      product.vendors_cc_draft_cert_verifier !== null &&
      progressPoints.length > 0 &&
      progressPoints[6].status === "Ready for Release"
    ) {
      return (
        <>
          <Col sm={12} md={8} lg={9}>
            <Row>
              <Col sm={12} className="mt-3">
                <ProductDetailsHeaderInfo
                  product={product}
                  projpps={projpps}
                  maintenances={maintenances}
                  setShow={setShow}
                  certificate={certificate}
                  downloadFile={downloadFile}
                  validatorFinalReview={true}
                  refresh={refresh}
                />
              </Col>
              <Col sm={12} className="mt-3">
                <ProductDetailsSubheader
                  product={product}
                  loadProject={loadProject}
                />
              </Col>
              <Col sm={12}>
                <h5 className="fw-bold mt-3">PROGRESS POINTS</h5>
              </Col>
              {progressPoints.length > 0 && (
                <ProgressPoints
                  progressPoints={progressPoints}
                  getEditButton={getEditButton}
                  showButtons={showButtons}
                  product={product}
                />
              )}
              <Col sm={12} className="mt-3" id="accordion-section">
                <PostingAccordion
                  project={product}
                  refetch={loadProject}
                  readyToPostChecks={readyToPostChecks}
                  handleReadyToPostCheck={handleReadyToPostCheck}
                />
              </Col>
            </Row>
          </Col>
          <Col sm={12} md={4} lg={3}>
            <PostingSideInfo
              product={product}
              files={files}
              readyToPostChecks={readyToPostChecks}
              handleReadyToPostCheck={handleReadyToPostCheck}
              nistReview={nistReview}
              setAlert={setAlert}
            />
          </Col>
        </>
      );
    } else if (permissions?.role_type === "Vendor") {
      return (
        <>
          <Col sm={12} md={8} lg={9}>
            <Row>
              <Col sm={12} className="mt-3">
                {progressPoints[6]?.status === "Ready for Release" ? (
                  <PostingHeader
                    product={product}
                    refetch={loadProject}
                    projpps={projpps}
                    validationReport={
                      validationReport.file_label
                        ? validationReport
                        : finalDraftValidationReport
                    }
                    vendorSignedCheckout={vendorSignedCheckout}
                    pcl={signedCheckIn}
                    securityTarget={securityTarget}
                    certificate={certificate}
                    assuranceActivityReport={assuranceActivityReport}
                    setShowCertificate={setShowCertificate}
                  />
                ) : (
                  <ProductDetailsHeaderInfo
                    product={product}
                    projpps={projpps}
                    maintenances={maintenances}
                    setShow={setShow}
                    certificate={certificate}
                    downloadFile={downloadFile}
                    refresh={refresh}
                  />
                )}
              </Col>
              <Col sm={12} className="mt-3" id="accordion-section">
                <NonEditableAccordion project={product} />
              </Col>
            </Row>
          </Col>
          <Col sm={12} md={4} lg={3}>
            <PostingSideInfo
              product={product}
              files={files}
              readyToPostChecks={readyToPostChecks}
              handleReadyToPostCheck={handleReadyToPostCheck}
              readOnly={true}
              nistReview={nistReview}
              setAlert={setAlert}
            />
          </Col>
        </>
      );
    } else if (
      // niap posting view
      permissions.role_type === "NIAP" &&
      progressPoints.length > 0 &&
      progressPoints[7].status === "In Progress"
    ) {
      return (
        <>
          <Col sm={12} md={8} lg={9}>
            <Row>
              <Col sm={12} className="mt-3">
                <ProductDetailsHeaderInfo
                  product={product}
                  projpps={projpps}
                  maintenances={maintenances}
                  setShow={setShow}
                  certificate={certificate}
                  downloadFile={downloadFile}
                  refresh={refresh}
                />
              </Col>
              <Col sm={12} className="mt-3">
                <ProductDetailsSubheader
                  product={product}
                  loadProject={loadProject}
                />
              </Col>
              <Col sm={12}>
                <h5 className="fw-bold mt-3">PROGRESS POINTS</h5>
              </Col>
              <ProgressPoints
                progressPoints={progressPoints}
                getEditButton={getEditButton}
                showButtons={showButtons}
                product={product}
              />
              <Col
                sm={12}
                id="posting-subheader-details"
                className="border-top border-dark"
              >
                <PostingSubheaderDetails
                  product={product}
                  validationReport={
                    validationReport.file_label
                      ? validationReport
                      : finalDraftValidationReport
                  }
                  projpps={projpps}
                  setShowCertificate={setShowCertificate}
                />
                {product?.validator_posting_verifier && (
                  <NiapClearPostingVerifier
                    product={product}
                    readyToPostChecks={readyToPostChecks}
                    setReadyToPostChecks={setReadyToPostChecks}
                    refetch={() => {
                      getProgressPoints();
                      loadProject();
                    }}
                  />
                )}
              </Col>
              <Col sm={12} className="mt-3" id="accordion-section">
                <PostingAccordion
                  project={product}
                  refetch={loadProject}
                  readyToPostChecks={readyToPostChecks}
                  handleReadyToPostCheck={handleReadyToPostCheck}
                />
              </Col>
            </Row>
          </Col>
          <Col sm={12} md={4} lg={3}>
            <SideInfo
              product={product}
              updateSidebarFiles={updateSidebarFiles}
              refetch={loadProject}
              validationTeam={validationTeam}
              nistReview={nistReview}
              setAlert={setAlert}
            />
          </Col>
        </>
      );
    } else if (product.certification_status === "Certified") {
      return (
        <>
          <Col sm={12} md={8} lg={9}>
            <Row>
              <Col sm={12} className="mt-3">
                <ProductDetailsHeaderInfo
                  product={product}
                  projpps={projpps}
                  maintenances={maintenances}
                  setShow={setShow}
                  certificate={certificate}
                  downloadFile={fetchBlankCertification}
                  refresh={refresh}
                />
              </Col>
              <Col sm={12} className="mt-3">
                <ProductDetailsSubheader
                  product={product}
                  loadProject={loadProject}
                />
              </Col>
              <Col sm={12}>
                <h5 className="fw-bold mt-3">PROGRESS POINTS</h5>
              </Col>
              {progressPoints.length > 0 && (
                <ProgressPoints
                  progressPoints={progressPoints}
                  getEditButton={getEditButton}
                  showButtons={showButtons}
                  product={product}
                />
              )}
              {progressPoints.length > 0 &&
                progressPoints[6].status === "Ready for Release" &&
                isMemberOfAssignedLab && (
                  <Col
                    sm={12}
                    className="mt-3 border-top border-bottom border-dark"
                    id="pcl-section"
                  >
                    <PCLReviewDetails
                      product={product}
                      labSignedCheckout={labSignedCheckout}
                      validationReport={
                        validationReport.file_label
                          ? validationReport
                          : finalDraftValidationReport
                      }
                      certificate={certificate}
                      refetch={() => {
                        loadProject();
                      }}
                    />
                  </Col>
                )}
              <Col sm={12} className="mt-3" id="accordion-section">
                <ProjectDetailsAccordion
                  id={id}
                  project={product}
                  refetch={loadProject}
                  assignedOrg={isMemberOfAssignedLab}
                  permissions={permissions}
                />
                <Notes item={product} note_type="product" id={id} />
              </Col>
            </Row>
          </Col>
          <Col sm={12} md={4} lg={3}>
            {permissions?.role_type === "NIAP" ||
            permissions?.role_type === "Validator" ||
            permissions?.role_type === "Lab" ? (
              <SideInfo
                product={product}
                updateSidebarFiles={updateSidebarFiles}
                refetch={loadProject}
                validationTeam={validationTeam}
                nistReview={nistReview}
                setAlert={setAlert}
              />
            ) : (
              <PostingSideInfo
                product={product}
                files={files}
                readyToPostChecks={readyToPostChecks}
                handleReadyToPostCheck={handleReadyToPostCheck}
                validationTeam={validationTeam}
                readOnly={true}
                nistReview={nistReview}
                setAlert={setAlert}
              />
            )}
          </Col>
        </>
      );
    } else {
      //regular project details page
      return (
        <>
          <Col sm={12} md={8} lg={9}>
            <Row>
              <Col sm={12} className="mt-3">
                <ProductDetailsHeaderInfo
                  product={product}
                  projpps={projpps}
                  maintenances={maintenances}
                  setShow={setShow}
                  certificate={certificate}
                  downloadFile={downloadFile}
                  refresh={refresh}
                />
              </Col>
              <Col sm={12} className="mt-3">
                <ProductDetailsSubheader
                  product={product}
                  loadProject={loadProject}
                />
              </Col>
              <Col sm={12}>
                <h5 className="fw-bold mt-3">PROGRESS POINTS</h5>
              </Col>
              {progressPoints.length > 0 && (
                <ProgressPoints
                  progressPoints={progressPoints}
                  getEditButton={getEditButton}
                  showButtons={showButtons}
                  product={product}
                />
              )}
              {progressPoints.length > 0 &&
                progressPoints[6].status === "Ready for Release" &&
                isMemberOfAssignedLab && (
                  <Col
                    sm={12}
                    className="mt-3 border-top border-bottom border-dark"
                    id="pcl-section"
                  >
                    <PCLReviewDetails
                      product={product}
                      labSignedCheckout={labSignedCheckout}
                      validationReport={
                        validationReport.file_label
                          ? validationReport
                          : finalDraftValidationReport
                      }
                      certificate={certificate}
                      refetch={() => {
                        loadProject();
                      }}
                    />
                  </Col>
                )}
              <Col sm={12} className="mt-3" id="accordion-section">
                <ProjectDetailsAccordion
                  id={id}
                  project={product}
                  refetch={loadProject}
                  assignedOrg={isMemberOfAssignedLab}
                  permissions={permissions}
                />
                <Notes item={product} note_type="product" id={id} />
              </Col>
            </Row>
          </Col>
          <Col sm={12} md={4} lg={3}>
            <SideInfo
              product={product}
              updateSidebarFiles={updateSidebarFiles}
              refetch={loadProject}
              validationTeam={validationTeam}
              nistReview={nistReview}
              setAlert={setAlert}
            />
          </Col>
        </>
      );
    }
  };

  return (
    <>
      <Container fluid>
        {showInitiateProgressModel && (
          <InitiateProgressModal
            show={showInitiateProgressModel}
            handleClose={() => handleCloseInitiateProgressModal()}
            product={product}
            progressPoint={currentPP}
            isEdit={progressPointEdit}
            isValidator={isValidator}
            refetch={() => {
              getProgressPoints();
              loadProject();
            }}
          />
        )}
        {showPPModal345 && (
          <ProgressPointModal345
            show={showPPModal345}
            handleClose={handleCloseProgressPointModal}
            product={product}
            progressPoint={currentPP}
            readOnly={progressPointModalReadOnly}
            allFiles={files}
            isEdit={progressPointEdit}
            refetch={() => {
              getProgressPoints();
              loadProject();
              getNistReview();
              updateFiles();
            }}
            setRefetchNotes={setRefetchNotes}
          />
        )}
        {showPPModal6 && (
          <ProgressPointModal6
            show={showPPModal6}
            handleClose={handleCloseProgressPointModal}
            product={product}
            protectionProfiles={projpps}
            progressPoint={currentPP}
            allFiles={files}
            isEdit={progressPointEdit}
            refetch={() => {
              getProgressPoints();
              loadProject();
              getNistReview();
              updateFiles();
            }}
            setRefetchNotes={setRefetchNotes}
          />
        )}
        {showPPModal7 && (
          <ProgressPointEditModal7
            show={showPPModal7}
            handleClose={handleCloseProgressPointModal}
            product={product}
            progressPoint={currentPP}
            allFiles={files}
            isEdit={progressPointEdit}
            refetch={() => {
              getProgressPoints();
              loadProject();
              getNistReview();
              updateFiles();
            }}
            setRefetchNotes={setRefetchNotes}
          />
        )}
        {showPPModal8 && (
          <ProgressPointEditModal8
            show={showPPModal8}
            handleClose={handleCloseProgressPointModal}
            product={product}
            progressPoint={currentPP}
            isEdit={progressPointEdit}
            refetch={() => {
              getProgressPoints();
              loadProject();
              getNistReview();
              updateFiles();
            }}
            setRefetchNotes={setRefetchNotes}
          />
        )}
        <Row>
          <Col sm={12} md={8} lg={9}>
            <Row>
              {alert.message && (
                <Alert
                  variant={alert.variant}
                  onClose={() => setAlert({})}
                  dismissible
                >
                  {alert.message}
                </Alert>
              )}
              {kickoffAlert.message && (
                <Alert
                  variant={kickoffAlert.variant}
                  dismissible
                  onClose={() => setKickoffAlert({})}
                >
                  {kickoffAlert.message} for VID: {product.v_id}
                </Alert>
              )}
            </Row>
          </Col>
        </Row>
        <Row>
          <WithdrawalTerminationFeedbackHeader
            product={product}
            setAlertInfo={setAlert}
            refetch={loadProject}
          />
        </Row>
        <Row>{showPageComponents()}</Row>
        <FileUploadModal
          show={show}
          updateFiles={updateFiles}
          type="product"
          id={id}
          vid={product.v_id}
        />
        <ValidatorKickoffModal
          show={showValidatorKickoffModal}
          onHide={handleCloseValidatorKickoffModal}
          product={product}
          refetch={() => {
            getProgressPoints();
            loadProject();
            setShowValidatorKickoffModal(false);
          }}
        />
        {showKickoffModal && (
          <ProductKickoffModal
            show={showKickoffModal}
            handleClose={handleCloseProductKickoffModal}
            product={product}
            productPersonnel={validationTeam}
            refetch={() => {
              getProgressPoints();
              loadProject();
            }}
          />
        )}
        <ScheduleMeetingModal
          show={showScheduleModal}
          product={product}
          handleClose={() => {
            setProgressPointEdit(false);
            setShowScheduleModal(false);
          }}
          isEdit={progressPointEdit}
          user={currentUser}
          meetingInfo={meeting}
          refetch={() => {
            getMeeting();
            getProgressPoints();
          }}
          progressPoint={currentPP}
          refetchProgress={() => getProgressPoints()}
          validationTeam={validationTeam}
        />
        <ValidatorCheckoutApprovalModal
          show={showValidatorApproveCheckoutModal}
          handleClose={() => {
            setShowValidatorApproveCheckoutModal(false);
          }}
          product={product}
          currentPP={currentPP}
          refetch={() => {
            getProgressPoints();
            updateFiles();
          }}
          setAlert={setAlert}
        />
        <ValidatorCheckoutRejectionModal
          show={showValidatorCheckoutModal}
          handleClose={() => {
            setShowValidatorCheckoutModal(false);
          }}
          project={product}
          currentPP={currentPP}
          refetch={() => {
            getProgressPoints();
            updateFiles();
          }}
          setAlert={setAlert}
          reasoning={rejectionType}
        />
        <ValidatorRecommendPostingModal
          show={showValidatorRecommendPostingModal}
          handleClose={() => {
            setShowValidatorRecommendPostingModal(false);
          }}
          product={product}
          files={files}
          refreshPage={refreshPage}
          refetch={() => {
            getProgressPoints();
            loadProject();
          }}
        />
        <CertificateModal
          show={showCertificate}
          handleClose={() => setShowCertificate(false)}
          product_id={product?.product_id}
          refetch={loadProject}
        />
        <Outlet />
      </Container>
    </>
  );
}

export default CheckProductPermDetails;
